import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { SetRequired } from 'type-fest';

import Locator from '../../../../shared/Locator';
import { isFreeTier } from '../../../../shared/subscriptions/utils';
import { window } from '../../../globals';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { nuc } from '../../../utils';
import CallToAction from '../../CallToAction';
import Button from '../../generic/Button/Button';
import FetcherIcon from '../../generic/FetcherIcon';
import Flex from '../../generic/Flex';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import { icons, IconText } from '../../generic/Icon';
import Link from '../../generic/Link/Link';
import List from '../../generic/List';
import PageHeadingBreadcrumbs from '../../generic/PageHeadingBreadcrumbs';
import ModalManagePackageLabels from '../../modals/ModalManagePackageLabels';
import { useManagePackageLabelsModal } from '../../projects/dependenciesV2/hooks/useManagePackageLabelsModal';
import PackageLabelsList from '../../shared/packageLabels/PackageLabelsList';
import TeamSelectionMenu from '../../shared/teams/TeamSelectionMenu';
import usePackageVersionDetails from '../hooks/usePackageVersionDetails';
import PackageDetailsMetadataDescription from '../PackageDetails/PackageDetailsMetadataDescription';
import { PackageInventoryLicenses } from '../shared';
import type { PackageLabelAssignmentWithName } from '../../../../shared/model-typings/packagelabelassignment';
import type { PackageInventoryParams } from '../PackageInventoryContainer';

import PackageVersionCTAMetadataLoading from './PackageVersionCTAMetadataLoading';

type Props = {
  params: SetRequired<PackageInventoryParams, 'packageLocator' | 'version'>
};

/**
 * PackageVersionCTA is the call-to-action for the package version
 * page (ie: `/package/:packageLocator/:version`).
 *
 * It displays the page heading "breadcrumbs" for packages, and
 * then also displays the package metadata.
 */
const PackageVersionCTA: VoidFunctionComponent<Props> = ({ params }) => {
  const { status, data } = usePackageVersionDetails(params);
  const stateQueryString = useSelector(PackageInventoryStore.selectors.getUnpaginatedPackagesStateQueryString);
  const packageMetadata = useSelector(PackageInventoryStore.selectors.getPackageMetadata);
  const { fetcher } = Locator.parse(data?.locator || '');
  const managePackageLabelsModal = useManagePackageLabelsModal();
  const plan = window.user?.organization?.stripe_plan;
  // Package label management is restricted to business and enterprise tiers
  const isPackageLabelsRestricted = isFreeTier({ plan });

  return (
    <>
      <CallToAction>
        <Flex justify="space-between">
          <PageHeadingBreadcrumbs
            truncateAt={40}
            crumbs={[
              {
                to: '/packages',
                text: 'Packages',
              },
              {
                content: <TeamSelectionMenu />,
              },
              {
                to: `/packages/${nuc(params.packageLocator)}?${stateQueryString}`,
                //  Use the package name if it exists, otherwise show loading/packageLocator
                // eslint-disable-next-line no-nested-ternary -- It's fine
                text: data?.name ? data.name : status.isLoading ? '...' : data?.name || params.packageLocator,
              },
              {
                text: params.version,
              },
            ]}
          />
          <PaidFeatureGate isRestricted={isPackageLabelsRestricted} hidePill>
            <Button
              onClick={() => {
                managePackageLabelsModal.onOpen({
                  context: { revision: params.packageLocator, title: packageMetadata?.name },
                });
              }}
              type="secondary"
              size="medium"
            >
              Manage Labels
            </Button>
          </PaidFeatureGate>
        </Flex>
        {(status.isLoading && !data) || !data ? (
          <PackageVersionCTAMetadataLoading />
        ) : (
          <>
            <div className="package-inventory__details__metadata">
              <div className="margin-bottom-sm">
                <h2 className="package-inventory__details__metadata__subtitle package-inventory__details__metadata__subtitle--dark">
                  {data.locator}
                </h2>
              </div>
              <PackageDetailsMetadataDescription description={data.description} hasDarkBackground />
            </div>
            <List.RowDetails isDark>
              <List.RowDetailsItem>
                <FetcherIcon fetcher={fetcher || ''} />
                {data?.packageManager && <span className="margin-left-xs">{data.packageManager}</span>}
              </List.RowDetailsItem>
              <List.RowDetailsIconItem icon={icons.folders} count={data.numberOfProjects}>
                Projects
              </List.RowDetailsIconItem>
              <PackageInventoryLicenses licenses={data.licenses} hasDarkBackground />
              <List.RowDetailsItem>
                <Link className="package-inventory__metadata__link" to={data.url} openInNewTab>
                  <IconText text="Homepage" icon={icons.externalLink} alignIcon="right" />
                </Link>
              </List.RowDetailsItem>
              <PackageLabelsList
                labels={
                  [
                    ...data.packageLabels,
                    ...(packageMetadata?.packageLabels || []),
                  ] as PackageLabelAssignmentWithName[]
                }
                hasDarkBackground
                enforceUnique
              />
            </List.RowDetails>
          </>
        )}
      </CallToAction>
      <ModalManagePackageLabels
        show={managePackageLabelsModal.isOpen}
        onHide={managePackageLabelsModal.onClose}
        context={managePackageLabelsModal.context}
        isGlobalEnabled
        isProjectEnabled={false}
        isRevisionEnabled={false}
      />
    </>
  );
};

export default PackageVersionCTA;
